// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * modes.ts — the three shapes a round of Rootbound can take.
 *
 * A mode must change how the game PLAYS, not a cosmetic number (principle #14).
 * Each of these changes something structural:
 *
 *  - Thicket  — the base game. Placement may only touch your own colour, so the
 *               opening is a build-up and contact is a decision you make.
 *  - Bramble  — placement may touch ANYTHING. That single change deletes the
 *               build-up: you can drop a piece onto the enemy's face on turn
 *               two, and the game is a contact brawl from the first move. Hand
 *               is smaller to match the shorter arc.
 *  - Deadwood — five occupied cells ring a Heartroot instead of six. Every
 *               Heartroot is a live threat from the midgame on, and the "slow
 *               but safe" lines that win Thicket simply lose here.
 *
 * The HOST's pick is what the room plays, frozen into the round start via
 * `roundOpts()`. `modeOf()` validates an id off the wire — an unknown id must
 * fall back, never reach the generator as `undefined`.
 */

export type PieceType = 'heartroot' | 'creeper' | 'burrower' | 'spindle' | 'warden' | 'sporeling';

export const PIECE_ORDER: readonly PieceType[] = [
  'heartroot',
  'creeper',
  'burrower',
  'spindle',
  'warden',
  'sporeling',
];

export type Hand = Record<PieceType, number>;

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** Starting hand, per player. Identical for both — this game has no asymmetry. */
  hand: Hand;
  /**
   * May a newly placed piece touch enemy pieces?
   *
   * false (Thicket/Deadwood) is the classic build-up rule. true (Bramble) is
   * the mode's whole identity, not a dial.
   */
  freePlacement: boolean;
  /** How many occupied neighbours ring a Heartroot and end the game. */
  ringThreshold: number;
  /** Hard cap on moves before the pressure tiebreak resolves it. */
  turnCap: number;
  /**
   * Does the second player get a free extra placement on its opening turn?
   *
   * This is the first-player compensation, and it exists because the sim
   * measured a 3:1 seat bias, not because it seemed likely. See BALANCE.md.
   */
  openingBonus: boolean;
  /**
   * Plies at which the ring threshold drops by one — "the wither".
   *
   * The early game is small and the late game is big (the hexbloom shape). A
   * Heartroot needs six cells to choke it at first and fewer as the colony
   * ages, so an early lead cannot be banked while a late position still
   * resolves. It adds ZERO state: a pure function of the ply counter, so P2P
   * sync is untouched.
   */
  wither: readonly number[];
  /**
   * At the cap, does level pressure go to the player who moved SECOND?
   *
   * Not arbitrary and not a coin: the first player spent the whole game a tempo
   * ahead, so failing to break level with it is a failure. This is Rootbound's
   * komi, and the sim chose it over the obvious alternatives.
   */
  tieToSecond: boolean;
  /**
   * What happens when BOTH Heartroots end up choked on the same move.
   *
   * `'draw'` is the obvious rule and it is why the wither did not work at first:
   * as the threshold falls, both Heartroots cross it on the same turn, so a
   * mechanic that fixed cap-outs completely simply converted them into
   * simultaneous deaths. `'mover'` gives it to whoever closed the ring, which
   * is both decisive and the honest reading — you sprang the trap.
   */
  doubleRing: 'draw' | 'mover';
  /**
   * How many plies LATER the second player's Heartroot withers than the first's.
   *
   * The first-player compensation, and the reason it is this shape rather than
   * an extra opening placement: an extra placement is worth ~55 points of seat
   * swing, which overshoots a ~25 point problem into an 81/19 rout the other
   * way. This is the same idea made continuous — the elder root rots first —
   * so the sim could dial it to the measured gap instead of picking the nearest
   * discrete lever and hoping. Zero state: derived from the ply counter.
   */
  witherSkew: number;
}

const FULL_HAND: Hand = {
  heartroot: 1,
  creeper: 2,
  burrower: 2,
  spindle: 2,
  warden: 1,
  sporeling: 1,
};

/**
 * Deadwood and Bramble both measured a heavy blowout rate on the smaller hand —
 * 30–48% of games finished inside twenty half-moves. Widening the hand took
 * Bramble's short-game rate from 47.9% to 2.1%. Note this DIRECTLY CONTRADICTS
 * the round-1 finding that bigger hands were catastrophic; that measurement was
 * taken under `doubleRing: 'draw'`, where the extra material turned games into
 * simultaneous deaths instead of blowouts. A finding taken under an old rule
 * set has to be re-taken, which is the only reason this was caught.
 */
const DEEP_HAND: Hand = {
  heartroot: 1,
  creeper: 3,
  burrower: 2,
  spindle: 2,
  warden: 2,
  sporeling: 1,
};

const BRAMBLE_HAND: Hand = {
  heartroot: 1,
  creeper: 3,
  burrower: 2,
  spindle: 2,
  warden: 1,
  sporeling: 1,
};

export const MODES: Record<string, Mode> = {
  thicket: {
    id: 'thicket',
    name: 'Thicket',
    blurb: 'The full game. New pieces may only touch your own colour, so contact is a choice.',
    hand: FULL_HAND,
    freePlacement: false,
    ringThreshold: 6,
    turnCap: 120,
    openingBonus: false,
    wither: [30],
    tieToSecond: true,
    doubleRing: 'mover',
    witherSkew: 8,
  },
  bramble: {
    id: 'bramble',
    name: 'Bramble',
    blurb: 'Place against anything, including the enemy. No build-up — a brawl from move two.',
    hand: BRAMBLE_HAND,
    freePlacement: true,
    ringThreshold: 6,
    turnCap: 100,
    openingBonus: false,
    // NO WITHER, deliberately, and this is the one mode without one. With both
    // thresholds falling on the same ply, bramble resolves overwhelmingly by
    // SIMULTANEOUS ring, so `doubleRing` decides the game on a parity
    // technicality: the seat split cliffs from 83/17 to 24/76 across a two-ply
    // change in the skew. A lever with two values, neither fair. Free placement
    // already gives bramble its pace; it does not need the ring to close in.
    wither: [],
    tieToSecond: false,
    doubleRing: 'mover',
    witherSkew: 0,
  },
  deadwood: {
    id: 'deadwood',
    name: 'Deadwood',
    blurb: 'The rot spreads fast — a Heartroot chokes on five cells, then four.',
    hand: DEEP_HAND,
    freePlacement: false,
    // Reaches 5 (and then 4) via the wither instead of STARTING there. Starting
    // at 5 made every opening a live threat and the mode snowballed: the leader
    // at move 8 won 67–80%. Same identity, earned over the round.
    ringThreshold: 6,
    turnCap: 100,
    openingBonus: false,
    wither: [20, 36],
    tieToSecond: true,
    doubleRing: 'mover',
    witherSkew: 8,
  },
};

export const MODE_IDS: readonly string[] = ['thicket', 'bramble', 'deadwood'];

export const DEFAULT_MODE_ID = 'thicket';

/**
 * Resolve a mode id that may have come off the wire, out of localStorage, or out
 * of a URL. `MODES[id] || DEFAULT` is not safe here — `'constructor'` and
 * `'toString'` are truthy properties of every object, and either would reach the
 * rules as a Mode whose every field is undefined.
 */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE_ID];
}

export function handSize(hand: Hand): number {
  return PIECE_ORDER.reduce((n, p) => n + hand[p], 0);
}
